import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getInitials, cn } from '../../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';
import { Plus, X, ChevronLeft, ChevronRight, Eye, Trash2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Story Ring Component for feed
export const StoryRing = ({ user, hasUnseen, onClick, isOwn }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center gap-1 shrink-0"
    data-testid={`story-ring-${user.user_id}`}
  >
    <div className={cn(
      "p-0.5 rounded-full",
      hasUnseen 
        ? "bg-gradient-to-br from-primary via-pink-500 to-purple-500" 
        : "bg-muted"
    )}>
      <div className="p-0.5 bg-background rounded-full">
        <Avatar className="w-14 h-14 sm:w-16 sm:h-16">
          <AvatarImage src={user.user_avatar} />
          <AvatarFallback className="bg-primary/10 text-primary text-sm">
            {getInitials(user.display_name || user.username)}
          </AvatarFallback>
        </Avatar>
      </div>
    </div>
    <span className="text-xs truncate w-16 text-center">
      {isOwn ? 'Ou' : user.username}
    </span>
  </button>
);

// Add Story Button
export const AddStoryButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center gap-1 shrink-0"
    data-testid="add-story-btn"
  >
    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-secondary flex items-center justify-center border-2 border-dashed border-primary/50">
      <Plus className="w-6 h-6 text-primary" />
    </div>
    <span className="text-xs">Add</span>
  </button>
);

// Stories Bar for HomePage
export const StoriesBar = ({ onCreateStory }) => {
  const { user, token } = useAuth();
  const [storiesData, setStoriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedUserIndex, setSelectedUserIndex] = useState(0);

  const fetchStories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stories/feed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStoriesData(response.data);
    } catch (error) {
      console.error('Failed to fetch stories:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  const openViewer = (index) => {
    setSelectedUserIndex(index);
    setShowViewer(true);
  };

  const hasOwnStory = storiesData.some(s => s.user_id === user?.id);

  if (loading) {
    return (
      <div className="flex gap-3 overflow-x-auto py-4 px-4 scrollbar-hide">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="w-16 h-20 bg-secondary rounded-full animate-pulse shrink-0" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-3 overflow-x-auto py-4 px-4 scrollbar-hide border-b border-border/50">
        {!hasOwnStory && <AddStoryButton onClick={onCreateStory} />}
        {storiesData.map((userData, index) => (
          <StoryRing
            key={userData.user_id}
            user={userData}
            hasUnseen={userData.has_unseen}
            isOwn={userData.user_id === user?.id}
            onClick={() => openViewer(index)}
          />
        ))}
        {storiesData.length === 0 && hasOwnStory === false && (
          <p className="text-sm text-muted-foreground py-4">No stories</p>
        )}
      </div>

      {showViewer && (
        <StoryViewer
          storiesData={storiesData}
          initialUserIndex={selectedUserIndex}
          onClose={() => { setShowViewer(false); fetchStories(); }}
        />
      )}
    </>
  );
};

// Story Viewer Modal
export const StoryViewer = ({ storiesData, initialUserIndex, onClose }) => {
  const { user, token } = useAuth();
  const [userIndex, setUserIndex] = useState(initialUserIndex);
  const [storyIndex, setStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);

  const currentUserData = storiesData[userIndex];
  const currentStory = currentUserData?.stories[storyIndex];
  const isOwn = currentUserData?.user_id === user?.id;

  // Mark story as viewed
  useEffect(() => {
    if (currentStory && !isOwn) {
      axios.post(`${API}/stories/${currentStory.id}/view`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }
  }, [currentStory, isOwn, token]);

  // Progress timer
  useEffect(() => {
    setProgress(0);
    const duration = currentStory?.media_type === 'video' ? 15000 : 5000;
    const interval = 50;
    let elapsed = 0;

    timerRef.current = setInterval(() => {
      elapsed += interval;
      setProgress((elapsed / duration) * 100);
      
      if (elapsed >= duration) {
        nextStory();
      }
    }, interval);

    return () => clearInterval(timerRef.current);
  }, [userIndex, storyIndex]);

  const nextStory = () => {
    if (storyIndex < currentUserData.stories.length - 1) {
      setStoryIndex(storyIndex + 1);
    } else if (userIndex < storiesData.length - 1) {
      setUserIndex(userIndex + 1);
      setStoryIndex(0);
    } else {
      onClose();
    }
  };

  const prevStory = () => {
    if (storyIndex > 0) {
      setStoryIndex(storyIndex - 1);
    } else if (userIndex > 0) {
      setUserIndex(userIndex - 1);
      setStoryIndex(storiesData[userIndex - 1].stories.length - 1);
    }
  };

  const deleteStory = async () => {
    try {
      await axios.delete(`${API}/stories/${currentStory.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Move to next story or close
      if (currentUserData.stories.length === 1) {
        onClose();
      } else {
        nextStory();
      }
    } catch (error) {
      console.error('Failed to delete story:', error);
    }
  };

  if (!currentStory) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black" data-testid="story-viewer">
      {/* Progress bars */}
      <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-2">
        {currentUserData.stories.map((_, idx) => (
          <div key={idx} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all"
              style={{ 
                width: idx < storyIndex ? '100%' : idx === storyIndex ? `${progress}%` : '0%'
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="absolute top-6 left-0 right-0 z-20 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10 ring-2 ring-white">
            <AvatarImage src={currentUserData.user_avatar} />
            <AvatarFallback>{getInitials(currentUserData.display_name)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-white font-semibold text-sm">{currentUserData.username}</p>
            <p className="text-white/70 text-xs">
              {new Date(currentStory.created_at).toLocaleTimeString('ht', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isOwn && (
            <>
              <div className="flex items-center gap-1 text-white/80 text-sm">
                <Eye className="w-4 h-4" />
                <span>{currentStory.views_count}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-white rounded-full"
                onClick={deleteStory}
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="text-white rounded-full"
            onClick={onClose}
          >
            <X className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Story Content */}
      <div className="flex items-center justify-center h-full">
        {currentStory.media_type === 'video' ? (
          <video
            src={currentStory.media_url}
            className="max-w-full max-h-full object-contain"
            style={{ filter: currentStory.filter_style }}
            autoPlay
            playsInline
            muted
          />
        ) : (
          <img
            src={currentStory.media_url}
            alt="Story"
            className="max-w-full max-h-full object-contain"
            style={{ filter: currentStory.filter_style }}
          />
        )}
      </div>

      {/* Caption */}
      {currentStory.caption && (
        <div className="absolute bottom-20 left-0 right-0 text-center px-4">
          <p className="text-white text-lg font-medium bg-black/30 backdrop-blur-sm rounded-2xl px-4 py-2 inline-block">
            {currentStory.caption}
          </p>
        </div>
      )}

      {/* Navigation areas */}
      <button
        className="absolute left-0 top-0 bottom-0 w-1/3"
        onClick={prevStory}
      />
      <button
        className="absolute right-0 top-0 bottom-0 w-1/3"
        onClick={nextStory}
      />

      {/* Nav buttons for desktop */}
      {userIndex > 0 && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white rounded-full bg-black/30 hidden md:flex"
          onClick={prevStory}
        >
          <ChevronLeft className="w-6 h-6" />
        </Button>
      )}
      {userIndex < storiesData.length - 1 && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white rounded-full bg-black/30 hidden md:flex"
          onClick={nextStory}
        >
          <ChevronRight className="w-6 h-6" />
        </Button>
      )}
    </div>
  );
};

// Create Story Modal
export const CreateStoryModal = ({ open, onClose }) => {
  const { token } = useAuth();
  const [media, setMedia] = useState(null);
  const [mediaType, setMediaType] = useState('image');
  const [filter, setFilter] = useState('');
  const [caption, setCaption] = useState('');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const FILTERS = [
    { id: 'none', name: 'Normal', style: '' },
    { id: 'warm', name: 'Cho', style: 'sepia(20%) saturate(120%)' },
    { id: 'cool', name: 'Fre', style: 'hue-rotate(180deg) saturate(80%)' },
    { id: 'vintage', name: 'Vintage', style: 'sepia(40%) contrast(90%)' },
    { id: 'bw', name: 'N&B', style: 'grayscale(100%)' },
    { id: 'bright', name: 'Klè', style: 'brightness(120%) contrast(110%)' },
  ];

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setMedia(event.target.result);
      setMediaType(file.type.startsWith('video/') ? 'video' : 'image');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!media) return;
    setLoading(true);

    try {
      await axios.post(`${API}/stories`, {
        media_url: media,
        media_type: mediaType,
        filter_style: filter,
        caption
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onClose();
    } catch (error) {
      console.error('Failed to create story:', error);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMedia(null);
    setFilter('');
    setCaption('');
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) { reset(); onClose(); }}}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="create-story-modal">
        {!media ? (
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">Kreye Story</h2>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-40 rounded-2xl border-2 border-dashed"
              variant="outline"
            >
              <div className="text-center">
                <Plus className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                <p>Chwazi foto oswa videyo</p>
              </div>
            </Button>
          </div>
        ) : (
          <div className="flex flex-col h-[80vh]">
            {/* Preview */}
            <div className="flex-1 bg-black flex items-center justify-center relative">
              {mediaType === 'video' ? (
                <video
                  src={media}
                  className="max-w-full max-h-full object-contain"
                  style={{ filter }}
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              ) : (
                <img
                  src={media}
                  alt="Preview"
                  className="max-w-full max-h-full object-contain"
                  style={{ filter }}
                />
              )}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 text-white bg-black/30 rounded-full"
                onClick={reset}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Controls */}
            <div className="p-4 space-y-4 bg-background">
              {/* Filters */}
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {FILTERS.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.style)}
                    className={cn(
                      "shrink-0 text-center",
                      filter === f.style && "scale-110"
                    )}
                  >
                    <div className={cn(
                      "w-14 h-14 rounded-xl overflow-hidden border-2 mb-1",
                      filter === f.style ? "border-primary" : "border-transparent"
                    )}>
                      {mediaType === 'video' ? (
                        <video src={media} className="w-full h-full object-cover" style={{ filter: f.style }} muted />
                      ) : (
                        <img src={media} alt={f.name} className="w-full h-full object-cover" style={{ filter: f.style }} />
                      )}
                    </div>
                    <span className={cn("text-xs", filter === f.style ? "text-primary font-semibold" : "text-muted-foreground")}>
                      {f.name}
                    </span>
                  </button>
                ))}
              </div>

              {/* Caption */}
              <input
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption..."
                className="w-full px-4 py-2 rounded-full bg-secondary border-none focus:ring-2 focus:ring-primary"
                maxLength={100}
              />

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full rounded-full h-12"
                data-testid="publish-story-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Pibliye Story'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StoriesBar;
